/**
 * End of a quarter year (rulebook p.9–10), in three steps:
 *   1. every player meets the three demands or pays the penalty (R-008)
 *   2. balance of power in the senate: consul and vice-consul take bonus tiles (R-018, R-027)
 *   3. the board is refilled and a quarter-year tile is removed
 *
 * The procedure is resumable: it runs until a player has a genuine choice,
 * records where it stopped in `state.quarterEnd`, and continues from the
 * matching command (SatisfyDemand, ChooseBonusTile).
 */
import { addVp, fail, playerOf, succeed, updatePlayer } from './internal.js';
import { MANIFESTS } from './manifests.js';
import { finishGame } from './scoring.js';
const TRAJAN_ICON = {
    bread: 'bread',
    games: 'helmet',
    religion: 'flame',
};
/** Tiles this player could spend (or reuse) to meet `icon`. */
export function demandOptions(player, icon, usedTrajanTiles) {
    const out = [];
    for (const t of player.trajanDemandTiles) {
        if (t.icon === TRAJAN_ICON[icon] && !usedTrajanTiles.includes(t.id))
            out.push({ tileId: t.id, source: 'trajan' });
    }
    for (const t of player.forumTiles) {
        if (t.kind === 'demand' && t.icon === icon)
            out.push({ tileId: t.id, source: 'forum' });
    }
    for (const t of player.forumTiles) {
        if (t.kind === 'wild-demand')
            out.push({ tileId: t.id, source: 'wild' });
    }
    return out;
}
/** Votes for the balance of power: senate track position plus senate tiles. */
export function senateVotes(player) {
    const track = MANIFESTS.senateTrack.spaces[player.senate]?.votes ?? 0;
    const tiles = player.forumTiles.reduce((n, t) => n + (t.kind === 'senate' ? t.votes : 0), 0);
    return track + tiles;
}
/** Players ordered from most to least influential (ties: track position, then higher disc). */
export function senateRanking(state) {
    const height = (p) => state.board.senateStacks[p.senate]?.indexOf(p.id) ?? -1;
    return [...state.players]
        .sort((a, b) => senateVotes(b) - senateVotes(a) || b.senate - a.senate || height(b) - height(a))
        .map((p) => p.id);
}
// ---------------------------------------------------------------------------
// Entry and resumption
// ---------------------------------------------------------------------------
export function beginQuarterEnd(state, endedBy, events) {
    const qe = {
        step: 'demands',
        playerIndex: 0,
        demandIndex: 0,
        usedTrajanTiles: {},
        misses: {},
        endedBy,
    };
    return continueQuarterEnd({ ...state, quarterEnd: qe, pendingActions: [] }, events);
}
/** Run the procedure until a player must decide, or to the end. */
export function continueQuarterEnd(state, events) {
    let s = state;
    for (;;) {
        const qe = s.quarterEnd;
        if (!qe)
            return succeed(s, events);
        if (qe.step === 'demands') {
            const player = s.turnOrder[qe.playerIndex];
            if (player === undefined) {
                s = { ...s, quarterEnd: { ...qe, step: 'senate' } };
                continue;
            }
            if (qe.demandIndex >= s.demands.length) {
                const misses = qe.misses[String(player)] ?? 0;
                const vp = MANIFESTS.scoring.demandPenaltyByMisses[misses] ?? 0;
                if (misses > 0) {
                    s = addVp(s, player, vp);
                    events.push({ type: 'DemandPenalty', player, misses, vp });
                }
                s = { ...s, quarterEnd: { ...qe, playerIndex: qe.playerIndex + 1, demandIndex: 0 } };
                continue;
            }
            const icon = s.demands[qe.demandIndex].icon;
            const options = demandOptions(playerOf(s, player), icon, qe.usedTrajanTiles[String(player)] ?? []);
            if (options.length > 1) {
                return succeed({ ...s, phase: { kind: 'quarterEnd.demands', player, demandIndex: qe.demandIndex } }, events);
            }
            const r = meetDemand(s, player, qe.demandIndex, options[0] ?? null, events);
            if (!r.ok)
                return r;
            s = r.state;
            continue;
        }
        if (qe.step === 'senate') {
            const ranking = senateRanking(s);
            const consul = ranking[0];
            const vice = ranking[1];
            const votes = {};
            for (const p of s.players)
                votes[String(p.id)] = senateVotes(p);
            events.push({ type: 'SenateBalance', votes, consul, viceConsul: vice });
            return succeed({ ...s, phase: { kind: 'quarterEnd.consulChoice', player: consul } }, events);
        }
        // refill
        const r = refill(s, events);
        return r;
    }
}
function meetDemand(state, player, demandIndex, option, events) {
    const qe = state.quarterEnd;
    const icon = state.demands[demandIndex].icon;
    let s = state;
    const key = String(player);
    if (!option) {
        events.push({ type: 'DemandUnmet', player, demandIndex, icon });
        s = { ...s, quarterEnd: { ...qe, misses: { ...qe.misses, [key]: (qe.misses[key] ?? 0) + 1 } } };
    }
    else if (option.source === 'trajan') {
        const used = [...(qe.usedTrajanTiles[key] ?? []), option.tileId];
        s = { ...s, quarterEnd: { ...qe, usedTrajanTiles: { ...qe.usedTrajanTiles, [key]: used } } };
        events.push({
            type: 'DemandMet',
            player,
            demandIndex,
            icon,
            tileId: option.tileId,
            source: 'trajan',
        });
    }
    else {
        const tile = playerOf(s, player).forumTiles.find((t) => t.id === option.tileId);
        s = updatePlayer(s, player, (pl) => ({
            ...pl,
            forumTiles: pl.forumTiles.filter((t) => t.id !== tile.id),
        }));
        s = { ...s, removed: { ...s.removed, forum: [...s.removed.forum, tile] } };
        events.push({
            type: 'DemandMet',
            player,
            demandIndex,
            icon,
            tileId: tile.id,
            source: option.source,
        });
    }
    const next = s.quarterEnd;
    return succeed({ ...s, quarterEnd: { ...next, demandIndex: demandIndex + 1 } }, events);
}
// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------
export function satisfyDemand(state, actor, demandIndex, tileId) {
    const phase = state.phase;
    if (phase.kind !== 'quarterEnd.demands')
        return fail('WRONG_PHASE', 'not the demands step');
    const qe = state.quarterEnd;
    if (!qe || demandIndex !== phase.demandIndex)
        return fail('ILLEGAL', `demand ${phase.demandIndex} is the one to meet`);
    const icon = state.demands[phase.demandIndex].icon;
    const option = demandOptions(playerOf(state, actor), icon, qe.usedTrajanTiles[String(actor)] ?? []).find((o) => o.tileId === tileId);
    if (!option)
        return fail('ILLEGAL', 'that tile cannot meet this demand');
    const events = [];
    const r = meetDemand(state, actor, phase.demandIndex, option, events);
    if (!r.ok)
        return r;
    return continueQuarterEnd(r.state, events);
}
export function chooseBonusTile(state, actor, side) {
    if (state.phase.kind !== 'quarterEnd.consulChoice')
        return fail('WRONG_PHASE', 'not the consul step');
    if (side !== 0 && side !== 1)
        return fail('ILLEGAL', 'side must be 0 or 1');
    const chosen = state.board.senateBonus[side];
    const other = state.board.senateBonus[side === 0 ? 1 : 0];
    if (!chosen)
        return fail('ILLEGAL', 'that bonus tile space is empty');
    const ranking = senateRanking(state);
    const vice = ranking[1];
    const events = [];
    let s = updatePlayer(state, actor, (pl) => ({
        ...pl,
        bonusTiles: [...pl.bonusTiles, { ...chosen, side: 'yellow' }],
    }));
    events.push({ type: 'BonusTileTaken', player: actor, tileId: chosen.id, side: 'yellow' });
    if (other) {
        s = updatePlayer(s, vice, (pl) => ({
            ...pl,
            bonusTiles: [...pl.bonusTiles, { ...other, side: 'grey' }],
        }));
        events.push({ type: 'BonusTileTaken', player: vice, tileId: other.id, side: 'grey' });
    }
    // Last quarter: the game ends here, senate positions intact for the tie-break (R-030).
    if (s.quarterTilesRemaining <= 1) {
        events.push({ type: 'QuarterEnded', quarter: s.time.quarter });
        const closed = {
            ...s,
            quarterTilesRemaining: 0,
            board: { ...s.board, senateBonus: [null, null] },
        };
        return succeed(finishGame(closed, events), events);
    }
    // Reset the senate: fewest votes at the bottom, consul on top.
    const stack = [...ranking].reverse();
    s = {
        ...s,
        players: s.players.map((p) => ({ ...p, senate: 0 })),
        board: {
            ...s.board,
            senateBonus: [null, null],
            senateStacks: s.board.senateStacks.map((_, i) => (i === 0 ? stack : [])),
        },
        quarterEnd: { ...s.quarterEnd, step: 'refill' },
    };
    events.push({ type: 'SenateReset', stack });
    return continueQuarterEnd(s, events);
}
// ---------------------------------------------------------------------------
// Refill
// ---------------------------------------------------------------------------
function refill(state, events) {
    const qe = state.quarterEnd;
    let s = state;
    const removedForum = [];
    const removedExtra = s.board.yellowSpaces.filter((t) => t !== null);
    // Senate tiles leave every mat; every forum tile still in the forum leaves the game.
    s = {
        ...s,
        players: s.players.map((p) => {
            const senateTiles = p.forumTiles.filter((t) => t.kind === 'senate');
            removedForum.push(...senateTiles);
            return { ...p, forumTiles: p.forumTiles.filter((t) => t.kind !== 'senate') };
        }),
    };
    removedForum.push(...s.board.forum.filter((t) => t !== null));
    // Refill the forum, the yellow spaces and empty, unoccupied provinces.
    let forumSupply = s.hidden.forumSupply;
    let extraSupply = s.hidden.extraActionSupply;
    let bonusBag = s.hidden.bonusBag;
    const take = (pile) => pile.length ? [pile[0], pile.slice(1)] : [null, pile];
    const forum = s.board.forum.map(() => {
        const [t, rest] = take(forumSupply);
        forumSupply = rest;
        return t;
    });
    const yellowSpaces = s.board.yellowSpaces.map(() => {
        const [t, rest] = take(extraSupply);
        extraSupply = rest;
        return t;
    });
    const provinces = {};
    for (const [id, pr] of Object.entries(s.board.provinces)) {
        const leaderPresent = s.players.some((p) => p.leader === id);
        if (pr.tile === null && pr.legionnaires.length === 0 && !leaderPresent) {
            const [t, rest] = take(forumSupply);
            forumSupply = rest;
            provinces[id] = { ...pr, tile: t };
        }
        else {
            provinces[id] = pr;
        }
    }
    const [b0, r0] = take(bonusBag);
    const [b1, r1] = take(r0);
    bonusBag = r1;
    const senateBonus = [b0, b1];
    const quarter = s.time.quarter;
    s = {
        ...s,
        board: {
            ...s.board,
            forum,
            yellowSpaces,
            provinces,
            senateBonus,
            ships: s.board.ships.map((sh) => ({ ...sh, side: 'colour' })),
        },
        hidden: { ...s.hidden, forumSupply, extraActionSupply: extraSupply, bonusBag },
        demands: [],
        removed: {
            ...s.removed,
            forum: [...s.removed.forum, ...removedForum],
            extraAction: [...s.removed.extraAction, ...removedExtra],
            demand: [...s.removed.demand, ...s.demands],
        },
        quarterTilesRemaining: s.quarterTilesRemaining - 1,
        time: { ...s.time, round: 1, quarter: quarter + 1, crossedStartThisTurn: false },
        quarterEnd: null,
    };
    events.push({ type: 'QuarterRefilled', quarter }, { type: 'QuarterEnded', quarter });
    const i = s.turnOrder.indexOf(qe.endedBy);
    const upcoming = s.turnOrder[(i + 1) % s.turnOrder.length];
    events.push({ type: 'TurnStarted', player: upcoming });
    return succeed({ ...s, current: upcoming, phase: { kind: 'awaitTray' } }, events);
}
//# sourceMappingURL=quarterEnd.js.map